import React, { useEffect, useState } from 'react';
import { compose } from 'ramda';
import Tables from 'components/Table';
import { reduxForm, Field, InjectedFormProps, change } from 'redux-form';
import { PolicyType } from 'store/modules/Policies/types';
import usePolicy from 'hooks/usePolicy';
import Fields from 'components/Fields';
import api from 'api';
import Router from 'components/Router/Router';
import Icons from 'components/Icons';
import Store from 'store/store';
import { Button } from 'components/Base';
import {
  errHelper,
  formatCurrency,
  formatPercent,
  unFormatCurrency,
  unFormatPercent
} from 'utils';
import TextInput from 'components/TextInput/TextInput';
import SwitchPoundPercantage from 'components/Fields/SwitchPoundPercantage/SwitchPoundPercantage';

const { TableCell, TableRow } = Tables;
const { useParams } = Router;
const { connect, actions } = Store;

enum discountTypeEnum {
  MONETARY = 1,
  PERCENTAGE = 2
}

interface Props extends InjectedFormProps {
  updatePolicy: (policy: PolicyType) => void;
  chargeOverrides: any[];
  setChargeOverrides: Function;
  selectedPolicy: number;
  setSelectedPolicy: Function;
  index: number;
  isApplyFixedChargeVisible: boolean;
}

const Policy = ({
  initialValues,
  updatePolicy,
  change,
  form,
  chargeOverrides,
  setChargeOverrides,
  selectedPolicy,
  setSelectedPolicy,
  index,
  isApplyFixedChargeVisible
}: Props) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { portfolio } = useParams() as any;
  const { humanizePolicyType, humanizePolicyPlatform, humanizePolicyProvider } =
    usePolicy();
  //@ts-ignore
  const { policy, options } = initialValues;
  const { recommendation_overriden } = policy;
  const [discountType, setDiscountType] = useState(
    policy.discount_type || discountTypeEnum.PERCENTAGE
  );
  const [discount, setDiscount] = useState(
    policy.discount_type === discountTypeEnum.MONETARY
      ? formatCurrency(policy.discount)
      : formatPercent(`${Number(policy.discount * 100)}` || 0)
  );

  const overridePlatform = (val: number) => {
    setIsProcessing(true);
    api
      .overridePolicy({ portfolio, policyId: policy.id, overrideId: val })
      .then(({ data }: any) => {
        updatePolicy(data);
        setIsProcessing(false);
      })
      .catch((err: any) => errHelper(err));
  };

  const removeOverride = () => {
    setIsProcessing(true);
    api.removeOverride({ portfolio, policyId: policy.id }).then(() => {
      api
        .getPolicy({ portfolio, policyId: policy.id })
        .then(({ data }: any) => {
          updatePolicy(data);
          // @ts-ignore
          change(form, 'policy', data);
          setIsProcessing(false);
        })
        .catch((err: any) => errHelper(err));
    });
  };
  const switchDiscountType = () => {
    if (discountType === discountTypeEnum.MONETARY) {
      setDiscountType(discountTypeEnum.PERCENTAGE);
      const unFormatted = unFormatCurrency(discount);
      const formatted = formatPercent(unFormatted);
      setDiscount(formatted);
    } else {
      setDiscountType(discountTypeEnum.MONETARY);
      const unFormatted = unFormatPercent(discount);
      const formatted = formatCurrency(unFormatted);
      setDiscount(formatted);
    }
  };

  useEffect(() => {
    const discounts = chargeOverrides;
    discounts[index] = {
      policy: policy.id,
      discount_type: discountType,
      discount:
        discountType === discountTypeEnum.PERCENTAGE
          ? (Math.abs(Number(unFormatPercent(discount))) / 100).toFixed(4)
          : unFormatCurrency(discount)
    };
    setChargeOverrides([...discounts]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount]);

  return (
    <TableRow key={policy.id}>
      <TableCell width="180px">{policy.owner.display_name}</TableCell>
      <TableCell width="100px">{policy.number}</TableCell>
      <TableCell width="150px">{humanizePolicyType(policy.type)}</TableCell>
      <TableCell width="150px">
        {humanizePolicyProvider(policy.provider)}
      </TableCell>
      <TableCell width="150px">
        {humanizePolicyPlatform(policy.platform)}
      </TableCell>
      {isApplyFixedChargeVisible && (
        <TableCell width="150px">
          <Field
            name="apply_fixed_charge"
            component="input"
            type="radio"
            checked={selectedPolicy === policy.id}
            onChange={() => setSelectedPolicy(policy.id)}
          />
        </TableCell>
      )}
      <TableCell style={{ maxWidth: '250px', minWidth: '250px' }}>
        {recommendation_overriden ? (
          <Button
            isLoading={isProcessing}
            color="primary"
            type="submit"
            onClick={removeOverride}
            style={{
              padding: '9px 15px'
            }}
          >
            Remove override
          </Button>
        ) : (
          <Icons.LoadingWrapper isLoading={isProcessing}>
            <Field
              name="override"
              fullWidth
              disabled={options.length < 1}
              onChange={({ target }: { target: any }) => {
                const { value } = target;
                overridePlatform(value);
              }}
              displayEmpty
              component={Fields.Select}
              options={options}
            />
          </Icons.LoadingWrapper>
        )}
      </TableCell>
      <TableCell
        style={{
          maxWidth: '250px',
          minWidth: '250px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center'
        }}
      >
        <TextInput
          formatter={
            discountType === discountTypeEnum.MONETARY
              ? formatCurrency
              : formatPercent
          }
          label=""
          value={discount}
          onChange={(e: string) => setDiscount(e)}
        />
        <div onClick={switchDiscountType}>
          <SwitchPoundPercantage
            input={{ value: discountType === discountTypeEnum.PERCENTAGE }}
          />
        </div>
      </TableCell>
    </TableRow>
  );
};

export default compose(
  connect(null, {
    updatePolicy: actions.caseActions.updatePolicies,
    change: change
  }),
  reduxForm({ enableReinitialize: true, keepDirtyOnReinitialize: true })
)(Policy);
